import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import { StreamingHelperService } from "./streaming-helper-service";
import { IWorkflowEngineStatus } from "./workflow-engine-service";

// Live Workflow-engine up/down status over a single long-lived SSE connection, replacing per-poll HTTP
// requests. The server pushes an initial snapshot on connect and thereafter only on a transition.
//
// Mirrors board-events-stream.ts: we use the fetch-based StreamingHelperService (NOT native EventSource,
// which can't send the designer's Bearer auth header) and own the reconnect loop. Scoped per-consumer via
// the returned disposer rather than a module singleton, since a single header badge drives it.

const RECONNECT_DELAY_MS: number = 3000;

function isWorkflowEngineStatus(data: unknown): data is IWorkflowEngineStatus {
  return (
    typeof data === "object" &&
    data !== null &&
    typeof (data as { isRunning?: unknown }).isRunning === "boolean" &&
    typeof (data as { port?: unknown }).port === "number"
  );
}

// Opens the status stream and invokes onStatus for every pushed snapshot until the returned disposer is
// called. Reconnects on drop. Never throws — stream/network failures fall through to the reconnect delay.
export function connectWorkflowEngineStatusStream(onStatus: (status: IWorkflowEngineStatus) => void): () => void {
  if (typeof fetch === "undefined") {
    return () => undefined; // SSR / unsupported environment
  }

  let streaming: boolean = true;
  let abort: AbortController | null = null;
  const url: string = `${CommonHelperFunctions.GetBackendUrl()}/workflowengine/status/stream`;

  const onEvent = (data: unknown): void => {
    if (isWorkflowEngineStatus(data)) {
      onStatus(data);
    }
  };

  const run = async (): Promise<void> => {
    while (streaming) {
      const controller: AbortController = new AbortController();
      abort = controller;
      try {
        await StreamingHelperService.streamEvents(url, onEvent, controller.signal);
      } catch {
        // network/stream error — fall through to the reconnect delay
      }
      if (!streaming) break;
      await new Promise<void>((resolve: () => void) => setTimeout(resolve, RECONNECT_DELAY_MS));
    }
  };

  void run();

  return (): void => {
    streaming = false;
    if (abort) {
      abort.abort();
      abort = null;
    }
  };
}
